// payoutShared.js
// Shared, presentation-only helpers for the team Payout report (styles, number
// formatting, canonical field list, PDF logo, payout-rule label). Kept separate
// so the team page and any future payout view render identically.

export const PAYOUT_RULE_LABELS = {
  1: "Fixed amount per month or % per unit (whichever is higher)",
  2: "Fixed amount per month or fixed per unit (whichever is higher)",
  3: "Fixed amount per month + % per unit",
  4: "Fixed amount per month + fixed per unit",
  5: "% per unit",
  6: "Fixed per unit",
  7: "Fixed amount per month",
};

// Human-readable payout rule with the exact configured rate, e.g.
// "% per unit — 10% + 18% GST".
export const formatPayoutRule = (meta) => {
  if (!meta) return "-";
  const base =
    PAYOUT_RULE_LABELS[meta.payoutRule] || String(meta.payoutRule || "-");
  const pct = Number(meta.commissionPercent);
  const cost = Number(meta.commissionCost);
  const gst = Number(meta.taxPercent);
  const gstSuffix = gst > 0 ? ` + ${gst}% GST` : "";
  if ([1, 3, 5].includes(meta.payoutRule) && pct > 0) {
    return `${base} — ${pct}%${gstSuffix}`;
  }
  if ([2, 4, 6].includes(meta.payoutRule) && cost > 0) {
    return `${base} — ${cost}/kWh${gstSuffix}`;
  }
  return base;
};

// Excel (xlsx-js-style) style tokens.
export const NAVY = "1F3864";
export const YELLOW = "FFF2A8";
export const LIGHT = "EAF0F8";
const THIN = {
  top: { style: "thin", color: { rgb: "D0D7E2" } },
  bottom: { style: "thin", color: { rgb: "D0D7E2" } },
  left: { style: "thin", color: { rgb: "D0D7E2" } },
  right: { style: "thin", color: { rgb: "D0D7E2" } },
};
export const S = {
  title: { font: { bold: true, sz: 16, color: { rgb: NAVY } } },
  subtitle: { font: { bold: true, sz: 12, color: { rgb: "333333" } } },
  meta: { font: { sz: 10, color: { rgb: "666666" } } },
  section: {
    font: { bold: true, sz: 11, color: { rgb: NAVY } },
    fill: { fgColor: { rgb: LIGHT } },
  },
  header: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: NAVY } },
    alignment: { horizontal: "center", vertical: "center" },
    border: THIN,
  },
  label: { font: { bold: false }, border: THIN },
  labelBold: { font: { bold: true }, border: THIN },
  money: { numFmt: "#,##0.00", alignment: { horizontal: "right" }, border: THIN },
  moneyBold: {
    numFmt: "#,##0.00",
    font: { bold: true },
    alignment: { horizontal: "right" },
    border: THIN,
  },
  number: { numFmt: "#,##0", alignment: { horizontal: "right" }, border: THIN },
  highlight: {
    numFmt: "#,##0.00",
    font: { bold: true },
    fill: { fgColor: { rgb: YELLOW } },
    alignment: { horizontal: "right" },
    border: THIN,
  },
  note: { font: { italic: true, sz: 9, color: { rgb: "666666" } } },
};

export const sumOverDelivered = (txns) =>
  (txns || []).reduce((a, t) => a + (t.overDeliveredAmount || 0), 0);

// Payout summary rows (Without GST / With GST), shared by the team page and the
// charger tab so both bridge identically. When collection is below the gross
// Charging Amount Collected (payment-gateway charges and/or over-delivery
// adjustments), the top rows bridge it down so the summaries reconcile.
export const buildPayoutSummaryRows = (
  summary,
  payout,
  meta,
  brand,
  overDeliveredTotal = 0,
  byPaymentType = []
) => {
  // Fee-rate suffix mirrors the payout-rule label shown below the summary (see
  // formatPayoutRule): percentage only for %-based rules, per-kWh for fixed-per-unit
  // rules, and no rate for fixed-monthly — so the fee label never shows a stray %.
  const pct = Number(meta?.commissionPercent) || 0;
  const cost = Number(meta?.commissionCost) || 0;
  const rule = meta?.payoutRule;
  let rateSuffix = "";
  if ([1, 3, 5].includes(rule) && pct > 0) rateSuffix = `${pct}% `;
  else if ([2, 4, 6].includes(rule) && cost > 0) rateSuffix = `${cost}/kWh `;
  const feeLabel = `${brand} CMS Fee (${rateSuffix}incl. GST)`;
  const gap =
    Number(summary.totalChargingCost) - Number(payout.totalCollectionByAmik);
  const od = Number(overDeliveredTotal) || 0;
  // RFID & OCPI are settled outside Amik and excluded from the payout collection
  // (the Amik CMS Fee still applies to them). Shown as separate bridge lines,
  // each sourced from byPaymentType so the with/without-GST amounts are exact.
  const excl = Number(payout?.excludedCharging) || 0;
  const exclWoGst = Number(payout?.excludedConsumption) || 0;
  const pg = gap - od - excl;
  // One "Less:" line per settled-outside type, in a stable order.
  const exclLine = (type, label) => {
    const p = (byPaymentType || []).find((x) => x.paymentType === type);
    if (!p) return null;
    const wGst = Number(p.totalChargingCost) || 0;
    if (wGst <= 0.005) return null;
    const woGst = wGst - (Number(p.totalTaxAmount) || 0);
    return { label, woGst: -woGst, wGst: -wGst };
  };
  const settledOutsideRows = [
    exclLine("RFID", "Less: RFID (settled outside Amik)"),
    exclLine("OCPI", "Less: OCPI (settled outside Amik)"),
  ].filter(Boolean);
  const rows = [];
  if (gap > 0.005) {
    rows.push({
      label: "Total Charging Amount Collected",
      woGst: summary.totalConsumptionCost,
      wGst: summary.totalChargingCost,
    });
    if (settledOutsideRows.length) {
      settledOutsideRows.forEach((r) => rows.push(r));
    } else if (excl > 0.005) {
      // Fallback for pre-split snapshots without per-type byPaymentType data.
      rows.push({
        label: "Less: RFID & OCPI (settled outside Amik)",
        woGst: -exclWoGst,
        wGst: -excl,
      });
    }
    if (pg > 0.005) rows.push({ label: "Less: PG Charges", woGst: -pg, wGst: -pg });
    if (od > 0.005)
      rows.push({
        label: "Less: Over-delivery Adjustment",
        woGst: -od,
        wGst: -od,
      });
  }
  rows.push({
    label: "Total Collection By Amik",
    woGst: payout.baseAmount,
    wGst: payout.totalCollectionByAmik,
  });
  rows.push({ label: feeLabel, woGst: payout.amikFee, wGst: payout.amikFee });
  rows.push({
    label: "Payout",
    woGst: payout.payoutExclGst,
    wGst: payout.payout,
    highlight: true,
  });
  return rows;
};

// DB timestamps are stored in UTC (returned as naive strings). Render them in IST
// with an explicit timezone label, independent of the viewer's browser zone, so a
// late-evening-UTC session (e.g. 2026-06-30 20:01 UTC = 2026-07-01 01:31 IST) reads
// as the IST date it is actually attributed to.
export const fmtTime = (v) => {
  if (!v) return "";
  // Normalise to a naive datetime then force-parse as UTC: drop any fractional
  // seconds and any trailing zone (Z or ±HH:MM) the backend might include.
  const iso =
    String(v)
      .trim()
      .replace(" ", "T")
      .replace(/\.\d+/, "")
      .replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "") + "Z";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(v);
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(d)
    .reduce((a, x) => ((a[x.type] = x.value), a), {});
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} IST`;
};

export const money = (n) => (Number(n) || 0).toFixed(2);
export const withComma = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Canonical Charging Summary field list. Price per kWh is optional: for a team
// (many chargers with possibly different tariffs) a blended average is misleading,
// so it is shown per charger instead and omitted here with { includePrice: false }.
// The three "collected by payment type" sections, shared by the team page and the
// charger tab and by their on-screen / Excel / PDF renderers so every surface shows
// the same breakdown: collected types (UPI/CARD/…), RFID, and OCPI segregated by
// roaming company. Each section carries its own rows, caption and column label.
// OCPI-by-company is aggregated from the transactions (each OCPI txn carries its
// ocpiCompany); its rows sum to the OCPI byPaymentType bucket.
export const buildPaymentTypeSections = (byPaymentType = [], transactions = []) => {
  const bpt = byPaymentType || [];
  const isSettledOutside = (t) => t === "RFID" || t === "OCPI";
  const norm = (p) => ({
    paymentType: p.paymentType,
    transactions: Number(p.transactions) || 0,
    totalChargingCost: Number(p.totalChargingCost) || 0,
  });
  const collected = bpt.filter((p) => !isSettledOutside(p.paymentType)).map(norm);
  const rfid = bpt.filter((p) => p.paymentType === "RFID").map(norm);
  const ocpiByCompany = Object.values(
    (transactions || [])
      .filter((t) => t.paymentType === "OCPI")
      .reduce((acc, t) => {
        const key = t.ocpiCompany || "Unknown";
        if (!acc[key]) {
          acc[key] = { paymentType: key, transactions: 0, totalChargingCost: 0 };
        }
        acc[key].transactions += 1;
        acc[key].totalChargingCost += Number(t.chargingCost) || 0;
        return acc;
      }, {})
  ).sort((a, b) => b.totalChargingCost - a.totalChargingCost);
  return [
    {
      key: "collected",
      title: "Charging Amount Collected by Payment Type",
      firstColLabel: "Payment Type",
      caption: "Collected by Amik (UPI, card, etc.).",
      emptyText: "No collected transactions.",
      rows: collected,
    },
    {
      key: "rfid",
      title: "RFID (settled outside Amik)",
      firstColLabel: "Payment Type",
      caption: "Not collected by Amik; the Amik CMS fee still applies.",
      emptyText: "No RFID transactions.",
      rows: rfid,
    },
    {
      key: "ocpi",
      title: "OCPI by Company (settled outside Amik)",
      firstColLabel: "OCPI Company",
      caption: "Roaming — settled by each partner; the Amik CMS fee still applies.",
      emptyText: "No OCPI transactions.",
      rows: ocpiByCompany,
    },
  ];
};

export const buildChargingSummary = (summary, opts = {}) => {
  const rows = [
    { label: "Total Transactions", value: summary.totalTransactions, int: true },
    { label: "Total kWh Consumed", value: summary.totalKWh },
  ];
  // Optional kWh breakdown by settlement bucket: payment collected (UPI/CARD/…)
  // vs OCPI vs RFID. Only shown when some energy was settled outside Amik, so a
  // pure pay-as-you-go team is not cluttered with a redundant single sub-row.
  const byPT = opts.byPaymentType || [];
  if (byPT.length) {
    const kwhOf = (type) => {
      const p = byPT.find((x) => x.paymentType === type);
      return p ? Number(p.totalKWh) || 0 : 0;
    };
    const rfidKWh = kwhOf("RFID");
    const ocpiKWh = kwhOf("OCPI");
    const collectedKWh = (Number(summary.totalKWh) || 0) - rfidKWh - ocpiKWh;
    if (rfidKWh > 0.0005 || ocpiKWh > 0.0005) {
      rows.push({
        label: "   • Payment Collected (kWh)",
        value: collectedKWh,
        sub: true,
      });
      if (ocpiKWh > 0.0005)
        rows.push({ label: "   • OCPI (kWh)", value: ocpiKWh, sub: true });
      if (rfidKWh > 0.0005)
        rows.push({ label: "   • RFID (kWh)", value: rfidKWh, sub: true });
    }
  }
  if (opts.includePrice !== false) {
    rows.push(
      { label: "Avg Price per kWh (excl. GST)", value: summary.avgPricePerKwExclGst },
      { label: "+ GST per kWh", value: summary.avgGstPerKw },
      {
        label: "= Avg Price per kWh (incl. GST)",
        value: summary.avgPricePerKw,
        bold: true,
      }
    );
  }
  rows.push(
    { label: "Total Consumption Cost", value: summary.totalConsumptionCost },
    { label: "Total GST", value: summary.totalTaxAmount },
    { label: "Total Charging Amount Collected", value: summary.totalChargingCost }
  );
  return rows;
};

// Loads the Amik logo as a PNG data URL for the PDF; resolves null on failure.
export const loadLogoDataUrl = () =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const w = img.naturalWidth || 240;
          const h = img.naturalHeight || 90;
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve({ dataUrl: canvas.toDataURL("image/png"), width: w, height: h });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = "/logo/amikLogo.svg";
    } catch (e) {
      resolve(null);
    }
  });
